import React from 'react';
import TopBar from './TopBar';
import { STORAGE_KEY, DEFAULT_CYCLE_STAGES, DEFAULT_CYCLE_LENGTH, DEFAULT_RATING_EMOJI_SET } from '../utils/constants';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import useDeviceDetection from '../hooks/useDeviceDetection';
import { validateCycleStages } from '../utils/cycle';

function Settings({ state, setState, setRoute }) {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { user } = useAuth();
  const { isMobile } = useDeviceDetection();

  const defaultCycleTracking = React.useMemo(() => ({
    enabled: false,
    cycleStartDate: null,
    stages: DEFAULT_CYCLE_STAGES.map((stage) => ({ ...stage })),
  }), []);

  const cycleTracking = state.cycleTracking && typeof state.cycleTracking === 'object'
    ? {
        enabled: Boolean(state.cycleTracking.enabled),
        cycleStartDate: state.cycleTracking.cycleStartDate || null,
        stages: Array.isArray(state.cycleTracking.stages) && state.cycleTracking.stages.length
          ? state.cycleTracking.stages
          : defaultCycleTracking.stages,
      }
    : defaultCycleTracking;

  const stagedList = React.useMemo(() => (
    (cycleTracking.stages || []).map((stage, index) => ({
      id: stage?.id || `stage-${index}`,
      name: stage?.name ?? '',
      length: stage?.length ?? '',
    }))
  ), [cycleTracking.stages]);

  const { errors: cycleStageErrors, totalLength: cycleStageTotalLength } = React.useMemo(
    () => validateCycleStages(stagedList, { strict: true }),
    [stagedList]
  );

  const updateCycleTracking = (updater) => {
    setState((s) => {
      const previous = s.cycleTracking && typeof s.cycleTracking === 'object' ? s.cycleTracking : defaultCycleTracking;
      const next = typeof updater === 'function' ? updater(previous) : { ...previous, ...updater };
      // Ensure stages are always properly structured
      const normalizedStages = Array.isArray(next.stages) && next.stages.length
        ? next.stages
        : defaultCycleTracking.stages;
      return {
        ...s,
        cycleTracking: {
          enabled: Boolean(next.enabled),
          cycleStartDate: next.cycleStartDate || null,
          stages: normalizedStages,
        },
      };
    });
  };

  const handleCycleToggle = (enabled) => {
    updateCycleTracking((prev) => ({
      ...prev,
      enabled,
      cycleStartDate: enabled ? (prev.cycleStartDate || new Date().toISOString().slice(0, 10)) : prev.cycleStartDate,
    }));
  };

  const handleCycleStageChange = (index, field, value) => {
    updateCycleTracking((prev) => {
      const prevStages = Array.isArray(prev.stages) && prev.stages.length
        ? prev.stages.map((stage, idx) => ({ ...stage, id: stage?.id || `stage-${idx}` }))
        : defaultCycleTracking.stages.map((stage) => ({ ...stage }));

      const updatedStages = prevStages.map((stage, idx) => {
        if (idx !== index) return stage;
        if (field === 'length') {
          const sanitized = value.replace(/[^0-9]/g, '');
          return {
            ...stage,
            length: sanitized === '' ? '' : parseInt(sanitized, 10),
          };
        }
        return {
          ...stage,
          name: value,
        };
      });

      return {
        ...prev,
        stages: updatedStages,
      };
    });
  };

  const addCycleStage = () => {
    updateCycleTracking((prev) => ({
      ...prev,
      stages: [
        ...(Array.isArray(prev.stages) && prev.stages.length ? prev.stages : defaultCycleTracking.stages.map((stage) => ({ ...stage }))),
        { id: `custom-${Date.now()}`, name: 'New Stage', length: 1 },
      ],
    }));
  };

  const removeCycleStage = (index) => {
    updateCycleTracking((prev) => {
      const prevStages = Array.isArray(prev.stages) && prev.stages.length
        ? prev.stages
        : defaultCycleTracking.stages;
      if (prevStages.length <= 1) {
        return prev;
      }
      return {
        ...prev,
        stages: prevStages.filter((_, idx) => idx !== index),
      };
    });
  };

  const resetCycleStages = () => {
    updateCycleTracking((prev) => ({
      ...prev,
      stages: DEFAULT_CYCLE_STAGES.map((stage) => ({ ...stage })),
    }));
  };

  const defaultRatingTracking = React.useMemo(() => ({
    enabled: false,
    emojiSet: [...DEFAULT_RATING_EMOJI_SET],
  }), []);

  const ratingTracking = state.ratingTracking && typeof state.ratingTracking === 'object'
    ? {
        enabled: Boolean(state.ratingTracking.enabled),
        emojiSet: Array.isArray(state.ratingTracking.emojiSet) && state.ratingTracking.emojiSet.length > 0
          ? state.ratingTracking.emojiSet
          : defaultRatingTracking.emojiSet,
      }
    : defaultRatingTracking;

  const updateRatingTracking = (updater) => {
    setState((s) => {
      const previous = s.ratingTracking && typeof s.ratingTracking === 'object' ? s.ratingTracking : defaultRatingTracking;
      const next = typeof updater === 'function' ? updater(previous) : { ...previous, ...updater };
      const normalizedEmojiSet = Array.isArray(next.emojiSet) && next.emojiSet.length > 0
        ? next.emojiSet
        : defaultRatingTracking.emojiSet;
      return {
        ...s,
        ratingTracking: {
          enabled: Boolean(next.enabled),
          emojiSet: [...normalizedEmojiSet],
        },
      };
    });
  };

  const handleRatingToggle = (enabled) => {
    updateRatingTracking((prev) => ({
      ...prev,
      enabled,
    }));
  };

  const handleEmojiChange = (index, newEmoji) => {
    updateRatingTracking((prev) => {
      const newEmojiSet = [...prev.emojiSet];
      newEmojiSet[index] = newEmoji;
      return {
        ...prev,
        emojiSet: newEmojiSet,
      };
    });
  };

  const addEmoji = () => {
    updateRatingTracking((prev) => ({
      ...prev,
      emojiSet: [...prev.emojiSet, '😊'],
    }));
  };

  const removeEmoji = (index) => {
    updateRatingTracking((prev) => {
      if (prev.emojiSet.length <= 1) {
        return prev;
      }
      return {
        ...prev,
        emojiSet: prev.emojiSet.filter((_, idx) => idx !== index),
      };
    });
  };

  const resetEmojiSet = () => {
    updateRatingTracking((prev) => ({
      ...prev,
      emojiSet: [...DEFAULT_RATING_EMOJI_SET],
    }));
  };

  const resetData = () => {
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  };

  return (
    <div className="p-4">
      <TopBar title="Settings" onBack={() => setRoute("home")} />
      <div className="mt-3">
        {/* User Info */}
        {user && (
          <div className="mb-4 p-3 rounded-md bg-gray-50 dark:bg-dark-bg border dark:border-dark-border">
            <div className="text-sm font-medium dark:text-dark-text">Account</div>
            <div className="text-sm text-gray-600 dark:text-dark-textSecondary mt-1">
              {user.email || user.user_metadata?.email || 'Signed in user'}
            </div>
          </div>
        )}
        
        <div className="p-3 rounded-md bg-white dark:bg-dark-surface border dark:border-dark-border">
          <div className="text-sm font-medium dark:text-dark-text">Theme</div>
          <select 
            value={state.theme} 
            onChange={(e) => setState((s) => ({ ...s, theme: e.target.value }))} 
            className="mt-2 w-full p-2 border dark:border-dark-border rounded-md bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text"
          >
            <option>Stoicism</option>
            <option>CBT</option>
            <option>Chakras</option>
            <option>Buddhism</option>
          </select>
          
          <div className="mt-4">
            <div className="text-sm font-medium dark:text-dark-text mb-2">Appearance</div>
            <label className="flex items-center gap-2">
              <input 
                type="checkbox" 
                checked={isDarkMode} 
                onChange={toggleDarkMode}
                className="rounded"
              />
              <span className="dark:text-dark-text">Dark mode</span>
            </label>
          </div>
          
          <div className="mt-3 text-sm">
            <label className="flex items-center gap-2">
              <input 
                type="checkbox" 
                checked={state.streakGoal} 
                onChange={(e) => setState((s) => ({ ...s, streakGoal: e.target.checked }))} 
                className="rounded"
              />
              <span className="dark:text-dark-text">Commit to a daily streak (enable daily reminders)</span>
            </label>
          </div>

          <div className="mt-4 border-t dark:border-dark-border pt-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium dark:text-dark-text">Cycle Tracking</div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={cycleTracking.enabled}
                  onChange={(e) => handleCycleToggle(e.target.checked)}
                  className="rounded"
                />
                <span className="dark:text-dark-text">Enable</span>
              </label>
            </div>

            {cycleTracking.enabled && (
              <div className="mt-3 space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-dark-textSecondary block mb-1">Cycle start date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={cycleTracking.cycleStartDate || ''}
                      onChange={(e) => updateCycleTracking({ cycleStartDate: e.target.value || null })}
                      className="mt-1 w-full p-2 border dark:border-dark-border rounded-md bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text cursor-pointer"
                    />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-dark-textSecondary mt-1">
                    Select the first day of your most recent cycle
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-dark-textSecondary">Cycle stages</label>
                    <button
                      type="button"
                      onClick={addCycleStage}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Add stage
                    </button>
                  </div>
                  <div className="mt-2 space-y-3">
                    {stagedList.map((stage, index) => (
                      <div key={stage.id || index} className="p-3 rounded-md border dark:border-dark-border bg-gray-50 dark:bg-dark-bg">
                        <div className="flex items-start gap-3">
                          <div className="flex-1">
                            <label className="block text-xs text-gray-500 dark:text-dark-textSecondary">Name</label>
                            <input
                              type="text"
                              value={stage.name}
                              onChange={(e) => handleCycleStageChange(index, 'name', e.target.value)}
                              className="mt-1 w-full p-2 border dark:border-dark-border rounded-md bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text"
                            />
                          </div>
                          <div className="w-28">
                            <label className="block text-xs text-gray-500 dark:text-dark-textSecondary">Length (days)</label>
                            <input
                              type="number"
                              min="1"
                              value={stage.length}
                              onChange={(e) => handleCycleStageChange(index, 'length', e.target.value)}
                              className="mt-1 w-full p-2 border dark:border-dark-border rounded-md bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text"
                            />
                          </div>
                        </div>
                        {stagedList.length > 1 && (
                          <div className="mt-2 text-right">
                            <button
                              type="button"
                              onClick={() => removeCycleStage(index)}
                              className="text-xs text-red-600 dark:text-red-400 hover:underline"
                            >
                              Remove stage
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 p-3 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                    <div className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Total Cycle Length: <span className="text-lg">{cycleStageTotalLength || 0}</span> days
                    </div>
                    <div className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                      Default: {DEFAULT_CYCLE_LENGTH} days
                    </div>
                  </div>

                  {cycleStageErrors.length > 0 && (
                    <ul className="mt-2 text-xs text-red-600 dark:text-red-400 space-y-1 list-disc list-inside">
                      {cycleStageErrors.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-3 text-right">
                    <button
                      type="button"
                      onClick={resetCycleStages}
                      className="text-xs text-gray-600 dark:text-dark-textSecondary hover:underline"
                    >
                      Reset to defaults
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 border-t dark:border-dark-border pt-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium dark:text-dark-text">Element Ratings</div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={ratingTracking.enabled}
                  onChange={(e) => handleRatingToggle(e.target.checked)}
                  className="rounded"
                />
                <span className="dark:text-dark-text">Enable</span>
              </label>
            </div>

            {ratingTracking.enabled && (
              <div className="mt-3 space-y-4">
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-dark-textSecondary">Rating emojis</label>
                    <button
                      type="button"
                      onClick={addEmoji}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Add emoji
                    </button>
                  </div>
                  <div className="mt-2 space-y-2">
                    {ratingTracking.emojiSet.map((emoji, index) => (
                      <div key={index} className="p-3 rounded-md border dark:border-dark-border bg-gray-50 dark:bg-dark-bg">
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">{emoji}</div>
                          <div className="flex-1">
                            <label className="block text-xs text-gray-500 dark:text-dark-textSecondary mb-1">Emoji</label>
                            <input
                              type="text"
                              value={emoji}
                              onChange={(e) => handleEmojiChange(index, e.target.value)}
                              className="w-full p-2 border dark:border-dark-border rounded-md bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text"
                              placeholder="Enter emoji"
                            />
                          </div>
                          {ratingTracking.emojiSet.length > 1 && (
                            <div className="flex items-end">
                              <button
                                type="button"
                                onClick={() => removeEmoji(index)}
                                className="text-xs text-red-600 dark:text-red-400 hover:underline px-2 py-1"
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 text-right">
                    <button
                      type="button"
                      onClick={resetEmojiSet}
                      className="text-xs text-gray-600 dark:text-dark-textSecondary hover:underline"
                    >
                      Reset to defaults
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-3 space-y-2">
            <button 
              onClick={() => setRoute("edit-utopia")} 
              className="w-full py-2 px-3 rounded-md bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/30"
            >
              Edit Utopia Settings
            </button>
            <button 
              className="w-full py-2 px-3 rounded-md bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/30" 
              onClick={resetData}
            >
              Reset prototype data
            </button>
          </div>
        </div>
        
        {/* Debug Section */}
        <div className="mt-4 p-3 rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
          <div className="text-sm font-medium dark:text-dark-text">Debug Info</div>
          <div className="text-xs text-gray-600 dark:text-dark-textSecondary mt-2 space-y-1">
            <div>Device: {isMobile ? 'Mobile' : 'Desktop'}</div>
            <div>Screen Width: {window.innerWidth}px</div>
            <div>Touch Support: {'ontouchstart' in window ? 'Yes' : 'No'}</div>
            <div>User Agent: {navigator.userAgent.slice(0, 50)}...</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
